import { useState, useEffect } from 'react';

/**
 * useFirmInfo — the single source of the CLIENT-FACING firm identity.
 *
 * The firm's public-facing name/phone/email/etc. come from firm_settings via
 * /api/public/firm-info. This is deliberately distinct from the product / code
 * name ("Virgo" / "VirgoLaw" / "AstraeaMesh"), which must NEVER appear on a
 * client-facing surface. Route every client-visible firm reference through here.
 *
 * This is also the per-tenant branding seam: when /api/public/firm-info becomes
 * tenant-scoped (custom-domain / firm-slug routing), every consumer of this hook
 * gets the right firm's identity with no further changes.
 *
 * Fetched once and cached across consumers for the session.
 */
let _cache = null;
let _inflight = null;

export default function useFirmInfo() {
  const [firm, setFirm] = useState(_cache || {});

  useEffect(() => {
    let alive = true;
    if (_cache) {
      setFirm(_cache);
      return;
    }
    if (!_inflight) {
      _inflight = fetch('/api/public/firm-info')
        .then((r) => (r.ok ? r.json() : {}))
        .then((d) => {
          _cache = d || {};
          return _cache;
        })
        .catch(() => ({}));
    }
    _inflight.then((d) => {
      if (alive) setFirm(d || {});
    });
    return () => {
      alive = false;
    };
  }, []);

  // firmName is safe to render directly; consumers supply their own fallback text.
  return { ...firm, firmName: (firm && firm.firm_name) || '' };
}
